// How long a unit of study takes, for planning a time-boxed session.
//
// ONE SET OF NUMBERS. The drill timer, the session planner and (later) the
// phone app's "about N min" all price a question the same way, so they live
// here, below all of them. src/lib/test-engine/mini-drill.ts re-exports the
// question paces so its existing importers don't move.
//
// Questions are priced at the official SAT's pace, which is not a flattering
// guess: 90 days of real sittings came out at about 80 seconds an answer,
// explanation reading included (time-session design, 2026-10). Review cards and
// Foundations cards are single recall items, so they are cheaper. A lesson goes
// by its measured reading time (src/lib/learn/read-time.ts), never a guess.
//
// The extended-time multiplier applies to anything timed against the SAT's
// clock: questions, review cards (each is a missed question coming back), skill
// checks and Checkpoint parts. It does not apply to Foundations cards or to a
// lesson, which have no clock and aren't SAT items.
//
// Pure, no imports beyond core.

import { SKILL_CHECK_COUNT } from './sage-action'

/** Reading & Writing: 32 min / 27 questions ≈ 71 s per question. */
export const SECONDS_PER_QUESTION_RW = 71
/** Math: 35 min / 22 questions ≈ 96 s per question. */
export const SECONDS_PER_QUESTION_MATH = 96
/** A mixed-section set (the daily drill) uses the average of the two paces. */
export const SECONDS_PER_QUESTION_MIXED = Math.round(
  (SECONDS_PER_QUESTION_RW + SECONDS_PER_QUESTION_MATH) / 2,
)

/** A spaced-repetition review card: one missed question, re-answered. */
export const SECONDS_PER_REVIEW_CARD = 60
/** A Foundations card: a single vocab / formula / grammar recall. */
export const SECONDS_PER_FOUNDATIONS_CARD = 30

/**
 * One timed Checkpoint part (module), before extended time. Mirrors
 * TEST_RUN_CONFIGS.CHECKPOINT.seconds in src/lib/test-engine/test-run-config.ts
 * (pinned equal by a test there — core can't import from src/).
 */
export const CHECKPOINT_PART_SECONDS = 15 * 60

/**
 * The button that starts a timed part's clock (the test directions dialog).
 * The planner's copy names it, so the two can't drift apart.
 */
export const START_PART_LABEL = 'Start module'

/** 'ALL' is a mixed-section set, the same vocabulary as the drill API. */
export type PaceSection = 'RW' | 'MATH' | 'ALL'

/**
 * A usable time multiplier: 1, 1.5 or 2 in practice. Anything missing, non-
 * finite or not positive is 1x — the same rule scaleDurationSec applies to a
 * legacy attempt, so a bad flag can never make a plan free or infinite.
 */
export function normalizeMultiplier(multiplier: number | null | undefined): number {
  return typeof multiplier === 'number' && Number.isFinite(multiplier) && multiplier > 0
    ? multiplier
    : 1
}

/** Seconds for one question in a section (null/unknown = the mixed pace). */
export function secondsPerQuestion(
  section: PaceSection | null | undefined,
  multiplier: number = 1,
): number {
  const base =
    section === 'RW'
      ? SECONDS_PER_QUESTION_RW
      : section === 'MATH'
        ? SECONDS_PER_QUESTION_MATH
        : SECONDS_PER_QUESTION_MIXED
  return base * normalizeMultiplier(multiplier)
}

/** Seconds for `count` questions in a section. */
export function questionsSeconds(
  count: number,
  section: PaceSection | null | undefined,
  multiplier: number = 1,
): number {
  return Math.max(0, count) * secondsPerQuestion(section, multiplier)
}

/** Seconds for one review card, extended time included. */
export function secondsPerReviewCard(multiplier: number = 1): number {
  return SECONDS_PER_REVIEW_CARD * normalizeMultiplier(multiplier)
}

/** Seconds for `count` review cards. */
export function reviewCardsSeconds(count: number, multiplier: number = 1): number {
  return Math.max(0, count) * secondsPerReviewCard(multiplier)
}

/** Seconds for `count` Foundations cards. No multiplier: they aren't timed SAT items. */
export function foundationsCardsSeconds(count: number): number {
  return Math.max(0, count) * SECONDS_PER_FOUNDATIONS_CARD
}

/** A lesson takes its measured reading time (honestReadMinutes), never less than a minute. */
export function lessonSeconds(readMinutes: number): number {
  const minutes = Number.isFinite(readMinutes) ? readMinutes : 1
  return Math.max(1, Math.round(minutes)) * 60
}

/** A learning-path skill check: always SKILL_CHECK_COUNT questions at the section's pace. */
export function skillCheckSeconds(
  section: PaceSection | null | undefined,
  multiplier: number = 1,
): number {
  return questionsSeconds(SKILL_CHECK_COUNT, section, multiplier)
}

/** One timed Checkpoint part on the student's clock (22.5 min at 1.5x). */
export function checkpointPartSeconds(multiplier: number = 1): number {
  return CHECKPOINT_PART_SECONDS * normalizeMultiplier(multiplier)
}
